import React, { useEffect, useState } from 'react';

const GRID_SIZE = 3;
const TILE_COUNT = GRID_SIZE * GRID_SIZE;

const createShuffledTiles = () => {
  const arr = Array.from({ length: TILE_COUNT }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  const isSolved = arr.every((tile, index) => tile === index);
  if (isSolved) [arr[0], arr[1]] = [arr[1], arr[0]];

  return arr;
};

const ImagePuzzle = ({ onSolved }) => {
  const [tiles, setTiles] = useState(() => createShuffledTiles());
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isSolved, setIsSolved] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [imageUrl, setImageUrl] = useState(`${process.env.PUBLIC_URL}/assets/puzzle.jpg`);

  const triggerSolveAnimation = () => {
    setIsSolved(true);
    setIsMerging(true);

    setTimeout(() => {
      setShowFullImage(true);
    }, 300);
  };

  useEffect(() => {
    if (!showFullImage) return;

    const timer = setTimeout(() => {
      setIsMerging(false);
    }, 450);

    return () => clearTimeout(timer);
  }, [showFullImage]);

  const handleTileClick = (index) => {
    if (isSolved) return;

    if (selectedIndex === null) {
      setSelectedIndex(index);
      return;
    }

    if (selectedIndex === index) {
      setSelectedIndex(null);
      return;
    }

    const nextTiles = [...tiles];
    [nextTiles[selectedIndex], nextTiles[index]] = [nextTiles[index], nextTiles[selectedIndex]];

    setTiles(nextTiles);
    setSelectedIndex(null);

    const solved = nextTiles.every((tile, tileIndex) => tile === tileIndex);
    if (solved) {
      triggerSolveAnimation();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#ffe7f2] to-[#ffd7e9] flex items-center justify-center px-4 py-8">
      <img
        src={`${process.env.PUBLIC_URL}/assets/puzzle.jpg`}
        alt=""
        className="hidden"
        onError={() => setImageUrl(`${process.env.PUBLIC_URL}/assets/rohit.jpg`)}
      />

      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-6 md:p-8 text-center border border-[#f4d8e7]">
        <h1 className="text-3xl md:text-4xl font-semibold text-[#2a1b23]">Dear Keira...</h1>

        <div className="mt-8 mx-auto relative w-full max-w-[360px] aspect-square">
          <div
            className={`absolute inset-0 grid grid-cols-3 gap-2 bg-[#fdf2f7] p-3 rounded-2xl transition-all duration-300 ${
              isMerging ? 'gap-0 p-0 rounded-xl' : ''
            } ${showFullImage ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'}`}
          >
            {tiles.map((tile, index) => {
              const row = Math.floor(tile / GRID_SIZE);
              const col = tile % GRID_SIZE;
              return (
                <button
                  key={`tile-${index}`}
                  onClick={() => handleTileClick(index)}
                  className={`border-2 transition-all duration-200 ${
                    selectedIndex === index ? 'border-[#ff4d97] scale-[1.02] z-10' : 'border-white'
                  } ${isMerging ? 'rounded-none shadow-none' : 'rounded-xl shadow-md'}`}
                  style={{
                    backgroundImage: `url(${imageUrl})`,
                    backgroundSize: `${GRID_SIZE * 100}% ${GRID_SIZE * 100}%`,
                    backgroundPosition: `${(col / (GRID_SIZE - 1)) * 100}% ${(row / (GRID_SIZE - 1)) * 100}%`,
                  }}
                  aria-label={`Puzzle tile ${index + 1}`}
                />
              );
            })}
          </div>

          <div
            className={`absolute inset-0 overflow-hidden rounded-xl transition-all duration-500 ${
              showFullImage ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
            }`}
          >
            <img src={imageUrl} alt="." className="h-full w-full object-cover" />
          </div>
        </div>

        {isSolved && (
          <div className="mt-8 space-y-3 transition-opacity duration-300 opacity-100">
            <p className="text-2xl text-[#2d1f29]">...you complete me.</p>
            <p className="text-3xl font-semibold text-[#2d1f29]">Will you be my Valentine?</p>
            <button
              onClick={onSolved}
              className="rounded-full bg-[#8a3fd1] text-white px-8 py-3 font-medium hover:bg-[#7630bc] transition-colors"
            >
              Yes
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImagePuzzle;
